import React, { useRef, useEffect } from "react";
import { useMount, useWindowSize } from "react-use";

import { useAnimationFrame } from "../hooks/useAnimationFrame";

/*

Imperitive ref and effect callback manager
---------------------------------------------
- Why? #1 easily manage mount & effect callbacks for use with imperitive elements (eg animtion libraries, PIXIJS, THREEJS)
- Why? #2 reuse the same core elements, but with different interactons. By purely accessing the imperitive refs, you can apply different interactions to the same base elements

- Regsiter refs easily with a label
- Choose a single state for the scene to trigger
- Run callbacks by label when the scene changes
- Access refs imperitively for side effects
- Trigger changes by updating the 'state' prop to match the effect key you want to load
- E.g loading this controller with state={'active'} with cleanup the current state and trigger the 'active' callback

      <SceneController 

        mount={({refs})=>{

          refs['element_ref'].style.color = 'red';

        }}

        state={(panelActive ? 'active' : 'inactive')}

        effects={
          {
            'active' : ({refs}) => {
              refs['element_ref'].style.color = 'blue';

              const cleanup = ()=>{

                // return code to run when state changes

              }

              return cleanup;

            },
            'inactive' : ({refs}) => {

              let el = refs['element_ref'];
              el.style.color = 'red';

              return () => el.style.color = '';
            }
          }
        }
      >

        {({register})=>{
      
      // Pass the regsiter function down to nested components...
          return <SimpleScene register={register} label={`Scene A`} />

        }}
      </SceneController>
    
    ...
  

    // Example scene where register function is used in ref callback
    function SimpleScene({register,label}){

        return <div ref={(el)=>register('main element',el)}>{label}</div>

    }


*/

const registerElement = (key, el, store) => {
  store[key] = el;
};

const getElement = (key, store) => {
  return store[key];
};

export const Scene = ({ children }) => {
  const store = useRef({});

  const ref = (key, el) => {
    registerElement(key, el, store.current);
  };
  const get = (key) => {
    getElement(key, store.current);
  };

  return children({ register: ref, get, store: store.current });
};

export const SceneController = React.memo(function SceneControllerComponent({
  mount,
  children,
  effects = {},
  state,
  frame,
  resize,
}) {
  const { width, height } = useWindowSize();
  const storeRef = useRef();
  const registerRef = useRef();
  const vars = useRef({});
  const cleanupPreviousState = useRef(null);

  // Run once on mount
  useMount(() => {
    let initVars = false;

    try {
      initVars = mount({
        refs: storeRef.current,
        register: registerRef.current,
        width,
        height,
        resize: (args = null) => {
          if (resize) {
            resize(
              { refs: storeRef.current, ...vars.current, width, height },
              args
            );
          }
        },
      });
    } catch (err) {
      // todo - set up error boundary
      // https://reactjs.org/docs/error-boundaries.html#:~:text=Error%20boundaries%20are%20React%20components,the%20whole%20tree%20below%20them.
      console.warn("Error in scene controller", err);
    }

    if (initVars) {
      vars.current = initVars;
    }
  });

  // Side effects trigger by object key that matches current state
  useEffect(() => {
    if (!effects) return;
    if (!state) return;
    if (effects.hasOwnProperty(state)) {
      // Clean up the last effect
      if (cleanupPreviousState.current)
        cleanupPreviousState.current({
          refs: storeRef.current,
          ...vars.current,
        });

      const callback = effects[state];

      cleanupPreviousState.current = callback({
        refs: storeRef.current,
        ...vars.current,
      });
    }
  }, [state, effects]);

  // Ticker function
  useAnimationFrame(() => {
    if (frame) {
      frame({
        refs: storeRef.current,
        width,
        height,
        ...vars.current,
      });
    }
  });

  // Window resize
  useEffect(() => {
    if (resize) {
      resize({ refs: storeRef.current, ...vars.current, width, height });
    }
  }, [width, height]);

  return (
    <Scene>
      {({ register, store }) => {
        storeRef.current = store;
        registerRef.current = register;

        return children({ register, state });
      }}
    </Scene>
  );
});
